"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getFeatureAppUrlParts = exports.autoFixFeatureAppUrl = exports.constructUrl = void 0;
const BASE_DOMAIN = process.env.BASE_DOMAIN; // optional, see function #calculateBaseDomain
const ENTRY_JS = process.env.ENTRY_JS || 'remote.js';
/**
 * Create URL from known facts of a Feature App.
 * Name and Version are minimal requirements
 */
const constructUrl = ({ appName, version, baseDomain = calculateBaseDomain(version), entryPoint = ENTRY_JS, }) => `https://${appName}.${baseDomain}/${version}/${entryPoint}`;
exports.constructUrl = constructUrl;
/**
 * Check that the Feature App URL contains the necessary bits.
 * Auto-fix the URL if possible.
 * @param url Address URL
 */
const autoFixFeatureAppUrl = (url) => {
    /**
     * Find GET variables and save them. They will be appended to the end after sanitation.
     */
    const GET_PARAMS_REGEX = /\?.*/;
    // Initialize params as empty
    let getParams = '';
    const getParamsFromURL = url.match(GET_PARAMS_REGEX);
    if (!!getParamsFromURL) {
        getParams = getParamsFromURL[0];
        // Remove any GET params to avoid conflicts with appending remote js
        url = url.replace(GET_PARAMS_REGEX, '');
    }
    /**
     * This next part will check if remote JS entry is set
     */
    // Checks that a URL ends with a JS file
    const CHECK_JS_REMOTE = /.[\.\w]+\.js$/;
    if (!url.match(CHECK_JS_REMOTE)) {
        // This URL doesn't end with `.js`.
        // Fixing it with certainty is impossible, but we'll assume that `remote.js` is missing
        // Note: this will not work well with GET params
        url = `${url}/${ENTRY_JS}`;
    }
    // Stitch GET params back
    url = `${url}${getParams}`;
    // Replace double slashes
    url = url.replace(/(\w)\/{2,}/gi, '$1/');
    return url;
};
exports.autoFixFeatureAppUrl = autoFixFeatureAppUrl;
/**
 * Get name and version from the feature app url
 * @param url Feature App URL
 */
const getFeatureAppUrlParts = (url) => {
    /**
     * Identify feature app parts from the URL
     * For example:
     *    URL: "https://my-feature-app.apps.one.audi.com/1.0.0/remote.js"
     *    Feature app name is group 3 - "audi-feature-app-user-menu"
     *    Feature app version is group 4 - "3"
     */
    const FEATURE_APP_FROM_URL_REGEXP = /(http[s]?:\/\/)?([^\/\s]+\/)([^\/\s]+)\/([^\/\s]+)(.*)/;
    const APP_NAME_FROM_DOMAIN_REGEXP = /^([\w-]+)./;
    const urlParts = url.match(FEATURE_APP_FROM_URL_REGEXP);
    if (!urlParts) {
        return {
            name: null,
            version: null,
        };
    }
    const domainParts = urlParts[2].match(APP_NAME_FROM_DOMAIN_REGEXP);
    return {
        name: domainParts[1],
        version: urlParts[3],
    };
};
exports.getFeatureAppUrlParts = getFeatureAppUrlParts;
/**
 * Calculates the base domain for loading a feature app.
 *
 * If BASE_DOMAIN env var is set, we take it, and skip the calculation.
 *
 * Otherweise, we use prod url to load the feature app if its version
 * is a stable one, e.g. v1.3.2.
 * If the feature app version is a non-stable one, we load the feature app
 * from the dev url.
 *
 * @todo This is a temporary solution and will be replace as soon as the connection
 * from Tenant Management Service to AppStore is established.
 * (because bundle urls are stored within the AppStore; AppStore is the single source
 * of truth; any magic here will be removed soon)
 *
 * @deprecated
 */
const calculateBaseDomain = (version) => {
    if (!!BASE_DOMAIN) {
        return BASE_DOMAIN;
    }
    const FEATURE_APP_STABLE_VERSION_REGEXP = /^v?\d+\.\d+\.\d+$/;
    return `cdn.${version?.match(FEATURE_APP_STABLE_VERSION_REGEXP) ? 'prod' : 'dev'}.one.audi.com`;
};
